import { api } from './api';
import { customerApi } from './customer-api';
import { isCustomerAuthenticated } from './customer-auth';

// ============================================================
// Public catalogue (no auth) — populates the booking wizard.
// ============================================================

export type CategoryFlowConfig = {
  // Residential property-size options. `recommended_hours` (optional)
  // pre-fills + recommends a duration on the "How many hours?" step
  // based on the size the customer picked.
  space_sizes?: Array<{ id: string; label: string; recommended_hours?: number }>;
  duration_hours?: Array<{ id: string; label: string }>;
  frequencies?: Array<{ id: string; label: string }>;
  commercial_spaces?: Array<{ id: string; label: string }>;
  questions?: Array<{
    id: string;
    label: string;
    type: 'YES_NO' | 'TEXT';
  }>;
  step_sequences?: Record<string, string[]>;
  addons_note?: string;
  // TREATMENT flows have no in-wizard backup step, so the checkout page
  // (/your-appointment) asks "Want a backup professional?" — but only when
  // the category opts in here. Admin-managed, like `questions`.
  ask_backup?: boolean;
};

export type ServiceCategory = {
  id: string;
  name: string;
  code: string;
  description: string | null;
  icon: string | null;
  image: string | null;
  image_url?: string | null;
  // Presigned/served URL for image_url — renders the category tile.
  image_url_preview_url?: string | null;
  booking_flow: 'SESSION' | 'TREATMENT';
  flow_config: CategoryFlowConfig | null;
  sort_order: number;
  status: 'ACTIVE' | 'INACTIVE';
};

export type ServiceItem = {
  id: string;
  name: string;
  code: string;
  description: string | null;
  image: string | null;
  pricing_unit: 'PER_HOUR' | 'PER_BOOKING';
  // Raw pro rate (ex-fee). Kept for any internal math, but customer-
  // facing UI should show the inclusive prices below instead.
  base_price: number;
  // Customer-facing inclusive prices (pro rate + platform fee on top),
  // computed server-side so the website never needs the fee %.
  //   display_price — the platform-standard inclusive price
  //   from_price     — cheapest opted-in pro's inclusive rate ("from £X")
  display_price?: number;
  from_price?: number;
  currency: string;
  duration_minutes: number;
  badge: string | null;
  highlights: string[] | null;
  properties: Record<string, any> | null;
  // Hair & Beauty: treatment needs a skin patch test before the
  // appointment. Drives the mandatory checkout confirmation.
  requires_patch_test?: boolean;
  sort_order: number;
  status: 'ACTIVE' | 'INACTIVE';
  category: {
    id: string;
    name: string;
    code: string;
    booking_flow: 'SESSION' | 'TREATMENT';
  };
  // Optional treatment sub-group (e.g. "Nails" within Beauty) used by
  // the category-first treatment picker (C2). Null when the treatment
  // isn't assigned to a group.
  treatment_category?: { id: string; name: string } | null;
};

export type TreatmentCategory = {
  id: string;
  name: string;
  icon: string | null;
  sort_order: number;
  status: 'ACTIVE' | 'INACTIVE';
};

export type ServiceAddonRow = {
  id: string;
  name: string;
  code: string;
  description: string | null;
  price: number;
  // Inclusive customer price (raw + platform fee). Falls back to `price`
  // when an older API build doesn't send it.
  display_price?: number;
  currency: string;
  is_per_block: boolean;
  block_minutes: number;
  sort_order: number;
  status: 'ACTIVE' | 'INACTIVE';
};

export async function listCategories() {
  const res = await api.get('/public/categories');
  return res.data as { status: boolean; data: ServiceCategory[] };
}

// Treatment sub-groups (e.g. "Nails", "Facials") under a top-level
// TREATMENT category (Beauty / Hair). Drives the category-first
// treatment picker (C2). Empty when the category has no groups, in
// which case the picker falls back to a flat treatment list.
export async function listTreatmentCategories(categoryId: string) {
  const res = await api.get('/public/treatment-categories', {
    params: { category_id: categoryId },
  });
  return res.data as { status: boolean; data: TreatmentCategory[] };
}

// ============================================================
// Public pro profile + reviews (no auth)
// ============================================================

export type PublicProfessional = {
  id: string;
  business_name: string | null;
  tagline: string | null;
  bio: string | null;
  cover_image_url: string | null;
  cover_image_preview_url?: string | null;
  years_of_experience: number;
  languages: string[];
  city: string | null;
  country_code: string | null;
  travel_radius_miles: number;
  // Pro's minimum booking value (Hair & Beauty); null = no minimum.
  minimum_booking_value?: number | null;
  rating_avg: number;
  rating_count: number;
  is_featured: boolean;
  // True when the admin has marked the pro DBS-checked (and it hasn't
  // expired) — drives the "DBS checked" badge next to "Verified".
  dbs_checked?: boolean;
  portfolio_images: Array<{
    url: string;
    caption?: string | null;
    preview_url?: string | null;
  }>;
  user: {
    first_name: string | null;
    last_name: string | null;
    profile_pic: string | null;
    profile_pic_preview_url?: string | null;
  } | null;
  services: Array<{
    id: string | null;
    name: string | null;
    duration_minutes: number;
    price: number;
    // Inclusive customer price (raw pro rate + platform fee); matches the
    // all-in basket. `price` is the raw rate. Falls back to `price`.
    display_price?: number;
    currency: string;
    pricing_unit: 'PER_HOUR' | 'PER_BOOKING';
    description: string | null;
    category: { id: string; name: string; code: string } | null;
  }>;
  // Pro-authored add-ons. `display_price` is the inclusive customer price;
  // `price` is the raw rate.
  addons?: Array<{
    id: string;
    name: string;
    duration_minutes: number;
    price: number;
    display_price?: number;
    currency: string;
  }>;
};

export type PublicReview = {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  customer: { first_name: string | null; last_name: string | null } | null;
  service: { id: string; name: string } | null;
};

export async function getPublicProfessional(id: string) {
  const res = await api.get(`/public/professional/${encodeURIComponent(id)}`);
  return res.data as {
    status: boolean;
    info: PublicProfessional;
    message: string;
  };
}

export type PublicReviewSort =
  | 'date_desc'
  | 'date_asc'
  | 'rating_desc'
  | 'rating_asc';

export async function listPublicReviewsForPro(
  id: string,
  opts?: {
    limit?: number;
    offset?: number;
    sort?: PublicReviewSort;
    rating?: number | null;
  },
) {
  const params: Record<string, unknown> = {
    limit: opts?.limit ?? 10,
    offset: opts?.offset ?? 0,
  };
  if (opts?.sort) params.sort = opts.sort;
  if (opts?.rating && opts.rating >= 1 && opts.rating <= 5) {
    params.rating = opts.rating;
  }
  const res = await api.get(
    `/public/reviews/professional/${encodeURIComponent(id)}`,
    { params },
  );
  return res.data as {
    status: boolean;
    data: PublicReview[];
    total_count: number;
    message: string;
  };
}

export type CustomerSubscriptionPlan = {
  id: string;
  name: string;
  tagline: string | null;
  monthly_price: number;
  yearly_price: number;
  currency: string;
  features: string[] | null;
  footer_note: string | null;
  sort_order: number;
  is_active: boolean;
  is_default: boolean;
};

export async function listCustomerSubscriptionPlans() {
  // Public endpoint — no auth, used by the booking basket's
  // "Subscribe & Save" offcanvas to show real plans instead of a
  // hard-coded mock.
  const res = await api.get('/subscription/for-dropdown', {
    params: { user_type: 'CUSTOMER' },
  });
  return res.data as {
    status: boolean;
    list: CustomerSubscriptionPlan[];
    message: string;
  };
}

export type VoucherPreview = {
  status: boolean;
  voucher: {
    id: string;
    code: string;
    title: string;
    description: string | null;
    discount_type: 'PERCENT' | 'FIXED';
    discount_value: number;
    currency: string;
    terms: string | null;
  };
  discount_amount: number;
};

export async function previewVoucherCode(code: string, subtotal: number) {
  const slug = encodeURIComponent(code.trim());
  // Assigned-only model: once the customer is signed in, validate the
  // code against THEIR own entitlements so the basket only ever shows a
  // discount they can actually redeem at checkout. Guests still get the
  // public "is this code real?" preview, but checkout enforces ownership.
  if (isCustomerAuthenticated()) {
    const res = await customerApi.get(`/customer/me/voucher/preview/${slug}`, {
      params: { subtotal },
    });
    return res.data as VoucherPreview;
  }
  const res = await api.get(`/public/voucher/preview/${slug}`, {
    params: { subtotal },
  });
  return res.data as VoucherPreview;
}

export async function listServices(categoryId?: string) {
  const res = await api.get('/public/services', {
    params: categoryId ? { category_id: categoryId } : undefined,
  });
  return res.data as { status: boolean; data: ServiceItem[] };
}

export async function listAddons(categoryId?: string) {
  const res = await api.get('/public/service-addons', {
    params: categoryId ? { category_id: categoryId } : undefined,
  });
  return res.data as { status: boolean; data: ServiceAddonRow[] };
}

export type BookingPricePreview = {
  subtotal: number;
  tax_percentage: number;
  tax_amount: number;
  charges: Array<{
    id: string;
    name: string;
    type: 'PERCENT' | 'FIXED';
    value: number;
    amount: number;
    currency: string;
  }>;
  total: number;
  currency: string;
};

// Returns the platform-side breakdown (tax + active booking_charges)
// for a given subtotal — drives the live total in the basket so the
// figure shown matches what Stripe will charge.
export async function previewBookingPricing(opts: {
  subtotal: number;
  currency?: string;
}) {
  const res = await api.get('/public/booking/preview', {
    params: {
      subtotal: opts.subtotal,
      currency: opts.currency ?? 'GBP',
    },
  });
  return res.data as { status: boolean; info: BookingPricePreview };
}

export type BookingQuoteItem = {
  service_id?: string | null;
  service_addon_id?: string | null;
  // A pro's own add-on — priced server-side off the pro's rate.
  professional_addon_id?: string | null;
  quantity: number;
  properties?: Record<string, any> | null;
};

export type BookingQuote = {
  currency: string;
  tax_name?: string;
  items: Array<{
    service_id: string | null;
    service_addon_id: string | null;
    name: string;
    unit_price: number;
    quantity: number;
    line_total: number;
    // Customer-facing prices = raw rate + platform fee on top (the +20%
    // model), matching the public profile / service picker. Display
    // these so the basket line equals the price the customer picked;
    // unit_price / line_total stay the raw pro rate.
    customer_unit_price?: number;
    customer_line_total?: number;
    currency: string;
    properties: Record<string, any> | null;
  }>;
  subtotal: number;
  // Assigned pro's minimum booking value (Hair & Beauty) + whether this
  // basket's services+addons subtotal clears it. Checkout hard-enforces.
  minimum_booking_value?: number | null;
  meets_minimum?: boolean;
  tax_percentage: number;
  tax_amount: number;
  // Platform/service fee added on top of the pro rate (+20% model).
  // VAT-inclusive — tax_amount is the VAT WITHIN this fee, not extra.
  platform_fee_amount: number;
  // Admin-editable label for the fee line (Settings.platform_fee_name).
  platform_fee_name?: string;
  charges_amount: number;
  charges_breakdown: Array<{
    id: string;
    name: string;
    type: string;
    value: number;
    amount: number;
  }>;
  grand_total_before_discounts: number;
  // Rate-lock token. The basket echoes this back on
  // createOneOff/createSubscription; if platform-wide pricing
  // (VAT %, platform fee %, active booking_charges) drifted between
  // basket render and checkout click, the backend 409s with code
  // PRICING_CHANGED + a fresh quote so the customer can re-confirm
  // the new total before Stripe is hit.
  pricing_version?: string;
  membership_discount: {
    amount: number;
    percentage: number;
    plan_name: string | null;
  };
  // False when ANY item in the basket is an EOT or Commercial clean —
  // those services are excluded from the £15/mo membership entirely
  // (no discount, no upsell). Defaults to true on the consumer side
  // when the backend omits the flag.
  membership_eligible?: boolean;
  voucher_discount: {
    amount: number;
    customer_voucher_id: string | null;
  };
  discount_amount: number;
  wallet_amount: number;
  card_amount: number;
  total_amount: number;
  mode: 'ONE_OFF' | 'SUBSCRIPTION';
};

// Single source of truth for the basket. Mirrors createOneOff /
// createSubscription's pricing recipe — returns the exact numbers
// Stripe will charge, including membership discount + voucher +
// wallet capping. The basket no longer does its own math; it just
// renders this response.
export async function previewBookingQuote(payload: {
  items: BookingQuoteItem[];
  mode?: 'ONE_OFF' | 'SUBSCRIPTION';
  currency?: string;
  // Selected pro — drives per-pro custom pricing so the basket total
  // matches what checkout will charge (the +20%-on-top model prices
  // off the pro's custom_price ?? base_price).
  professional_id?: string | null;
  customer_voucher_id?: string | null;
  voucher_code?: string | null;
  wallet_amount?: number;
}) {
  // customerApi here so the customer JWT (if any) rides along — the
  // backend's /public/booking/quote endpoint reads it best-effort to
  // apply membership / voucher / wallet for the signed-in customer.
  const res = await customerApi.post('/public/booking/quote', {
    items: payload.items,
    mode: payload.mode ?? 'ONE_OFF',
    currency: payload.currency ?? 'GBP',
    professional_id: payload.professional_id ?? null,
    customer_voucher_id: payload.customer_voucher_id ?? null,
    voucher_code: payload.voucher_code ?? null,
    wallet_amount: payload.wallet_amount ?? 0,
  });
  return res.data as { status: boolean; info: BookingQuote };
}

// ============================================================
// Public availability (no auth) — drives the SelectProfessional
// grid and the per-pro time-slot picker.
// ============================================================

export type AvailabilitySlot = { start: string; end: string };

export type AvailablePro = {
  id: string;
  business_name: string | null;
  rating_avg: number;
  rating_count: number;
  is_featured: boolean;
  // Pro's minimum booking value (Hair & Beauty); null = no minimum.
  minimum_booking_value?: number | null;
  cover_image_url: string | null;
  cover_image_preview_url?: string | null;
  user: { id: string; first_name: string; last_name: string } | null;
  slots_by_date: Record<string, AvailabilitySlot[]>;
};

export async function listAvailablePros(args: {
  service_id: string;
  date_from: string; // YYYY-MM-DD
  date_to: string; // YYYY-MM-DD
  duration_minutes?: number;
  // Optional customer location for radius filtering. Pass both or
  // neither — passing one without the other is ignored server-side.
  lat?: number | null;
  lng?: number | null;
  // Customer's town/city — matched against each pro's coverage_towns so a pro
  // who lists the searched town shows even if it's beyond their travel radius.
  city?: string | null;
}) {
  const res = await api.get('/public/availability/pros', {
    params: {
      service_id: args.service_id,
      date_from: args.date_from,
      date_to: args.date_to,
      ...(args.duration_minutes
        ? { duration_minutes: args.duration_minutes }
        : {}),
      ...(args.lat != null && args.lng != null
        ? { lat: args.lat, lng: args.lng }
        : {}),
      ...(args.city ? { city: args.city } : {}),
    },
  });
  return res.data as { status: boolean; data: AvailablePro[] };
}

// Fallback for "no pros free on your chosen date" — returns the N
// pros with the soonest alternative slot at-or-after `target_date`,
// each carrying their slot map. Used by SelectProfessional to surface
// "Closest alternatives" when the primary listing comes back empty.
export async function listNearestAvailablePros(args: {
  service_id: string;
  target_date: string; // YYYY-MM-DD
  duration_minutes?: number;
  lat?: number | null;
  lng?: number | null;
  city?: string | null;
  days_ahead?: number;
  limit?: number;
}) {
  const res = await api.get('/public/availability/nearest-pros', {
    params: {
      service_id: args.service_id,
      target_date: args.target_date,
      ...(args.duration_minutes
        ? { duration_minutes: args.duration_minutes }
        : {}),
      ...(args.lat != null && args.lng != null
        ? { lat: args.lat, lng: args.lng }
        : {}),
      ...(args.city ? { city: args.city } : {}),
      ...(args.days_ahead ? { days_ahead: args.days_ahead } : {}),
      ...(args.limit ? { limit: args.limit } : {}),
    },
  });
  return res.data as {
    status: boolean;
    data: Array<AvailablePro & { next_slot_at: string | null }>;
    target_date: string;
    // 'service' when the returned pros offer the exact requested
    // service; 'category' when the backend had to widen to any
    // sibling service in the same category (no pros offered the
    // exact one). Drives the heading on the alternatives panel.
    fallback_kind?: 'service' | 'category';
  };
}

export async function getProAvailability(args: {
  professional_id: string;
  date_from: string;
  date_to: string;
  duration_minutes: number;
}) {
  const res = await api.get(
    `/public/availability/pro/${encodeURIComponent(args.professional_id)}`,
    {
      params: {
        date_from: args.date_from,
        date_to: args.date_to,
        duration_minutes: args.duration_minutes,
      },
    },
  );
  return res.data as {
    status: boolean;
    data: {
      professional_id: string;
      slots_by_date: Record<string, AvailabilitySlot[]>;
    };
  };
}

// ============================================================
// Customer bookings (auth required) — one-off Stripe Checkout.
// ============================================================

export type BookingItem = {
  service_id?: string | null;
  service_addon_id?: string | null;
  quantity?: number;
  properties?: Record<string, any> | null;
};

export type CreateOneOffPayload = {
  service_category_id: string;
  service_id: string;
  professional_id?: string | null;
  scheduled_start: string; // ISO datetime
  duration_minutes?: number;
  address_line1: string;
  address_line2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  items: BookingItem[];
  customer_voucher_id?: string | null;
  // Human-typeable voucher code from the basket. Resolved to a
  // CustomerVoucher row server-side.
  voucher_code?: string | null;
  wallet_amount?: number;
  customer_note?: string | null;
  customer_note_images?: string[] | null;
  currency?: string;
  // Customer opted into a backup professional ("Want a backup?" → Yes).
  accept_backup?: boolean;
  // Customer-nominated preferred backup pros (ordered). The reassignment
  // search prefers these — in order — if the chosen pro can't make it.
  secondary_professional_ids?: string[];
  // Rate-lock token from the most-recent /quote response. Pass it so
  // the backend can 409 PRICING_CHANGED if platform-wide pricing
  // drifted between basket render and Book-and-Pay click.
  pricing_version?: string;
};

export type CheckoutResponse = {
  status: boolean;
  id: string;
  reference: string;
  checkout_url: string | null;
  stripe_session_id: string | null;
  requires_payment: boolean;
  // Only set on the wallet-covers-all path (`checkout_url: null`).
  // Lets the frontend pick "Booking confirmed" vs "Waiting for the
  // pro to accept" instead of guessing.
  booking_status?: string;
  card_amount?: number;
  wallet_amount?: number;
  total: number;
  currency: string;
  message: string;
};

export async function createOneOffCheckout(payload: CreateOneOffPayload) {
  const res = await customerApi.post(
    '/customer/me/bookings/checkout',
    payload,
  );
  return res.data as CheckoutResponse;
}

export type BookingConfirmResponse = {
  status: boolean;
  id: string;
  reference: string;
  booking_status: string;
  payment_status: string;
  scheduled_start: string;
  total_amount: number;
  currency: string;
};

export async function confirmBookingCheckout(sessionId: string) {
  const res = await customerApi.get(
    `/customer/me/bookings/confirm/${encodeURIComponent(sessionId)}`,
  );
  return res.data as BookingConfirmResponse;
}

export type BookingSummary = {
  id: string;
  reference: string;
  scheduled_start: string;
  duration_minutes: number;
  status: string;
  source: 'ONE_OFF' | 'SUBSCRIPTION';
  total_amount: number;
  wallet_amount: number;
  card_amount: number;
  currency: string;
  address_line1: string;
  created_at?: string;
  service: { id: string; name: string; image?: string | null } | null;
  service_category: {
    id: string;
    name: string;
    image_url?: string | null;
  } | null;
  professional: {
    id: string;
    business_name: string | null;
    user: { first_name: string; last_name: string };
  } | null;
};

export async function listMyBookings(opts?: {
  limit?: number;
  offset?: number;
  status?: string;
  scope?: 'upcoming' | 'past';
  q?: string;
  service_id?: string;
  date_from?: string;
  date_to?: string;
}) {
  const res = await customerApi.get('/customer/me/bookings', {
    params: {
      limit: opts?.limit ?? 10,
      offset: opts?.offset ?? 0,
      ...(opts?.status ? { status: opts.status } : {}),
      ...(opts?.scope ? { scope: opts.scope } : {}),
      ...(opts?.q ? { q: opts.q } : {}),
      ...(opts?.service_id ? { service_id: opts.service_id } : {}),
      ...(opts?.date_from ? { date_from: opts.date_from } : {}),
      ...(opts?.date_to ? { date_to: opts.date_to } : {}),
    },
  });
  return res.data as {
    status: boolean;
    data: BookingSummary[];
    total_count: number;
  };
}

export async function getBookingDetail(id: string) {
  const res = await customerApi.get(
    `/customer/me/bookings/info/${encodeURIComponent(id)}`,
  );
  return res.data as { status: boolean; info: any };
}

export type CancellationPreview = {
  status: boolean;
  // True only inside the charge windows (2–24h → 50%, <2h → 100%).
  will_charge: boolean;
  charge_pct: number;
  charge_amount: number;
  refund_amount: number;
  currency: string;
  window: 'FULL_REFUND' | 'PARTIAL' | 'NO_REFUND' | 'NONE';
};

// What cancelling NOW would refund vs. charge for this booking — used to
// warn the customer before they confirm. Read-only.
export async function getCancellationPreview(
  id: string,
): Promise<CancellationPreview> {
  const res = await customerApi.get(
    `/customer/me/bookings/${encodeURIComponent(id)}/cancellation-preview`,
  );
  return res.data as CancellationPreview;
}

export async function cancelBooking(id: string, reason?: string) {
  const res = await customerApi.put(
    `/customer/me/bookings/${encodeURIComponent(id)}/cancel`,
    { reason: reason ?? null },
  );
  return res.data as {
    status: boolean;
    booking_status: string;
    refund_outcome:
      | 'NONE'
      | 'WALLET_CREDIT'
      | 'CARD_REFUND'
      | 'PARTIAL_CARD_REFUND'
      | 'PARTIAL_WALLET_CREDIT';
    refund_amount: number;
    message: string;
  };
}

// Customer requests to move a CONFIRMED booking to a new time on the
// same pro's availability. The booking flips to PENDING_PROFESSIONAL_
// CONFIRM until the pro approves; declined/expired requests revert to
// the original time. `scheduledStartIso` is the chosen slot's start.
export async function rescheduleBooking(id: string, scheduledStartIso: string) {
  const res = await customerApi.put(
    `/customer/me/bookings/${encodeURIComponent(id)}/reschedule`,
    { scheduled_start: scheduledStartIso },
  );
  return res.data as {
    status: boolean;
    booking_status: string;
    message: string;
  };
}

// ============================================================
// Customer subscriptions — recurring bookings via Stripe Sub.
// ============================================================

export type CreateSubscriptionPayload = {
  service_category_id: string;
  service_id: string;
  preferred_professional_id?: string | null;
  frequency: 'DAILY' | 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY';
  // 0 (Sun) – 6 (Sat). Omitted for DAILY (runs every day — no weekday).
  day_of_week?: number;
  start_time: string; // HH:MM:SS — interpreted in `timezone` below
  // IANA timezone the wall-clock pick was made in (e.g.
  // "Europe/London"). Sent so the backend can compute
  // scheduled_start in the customer's local zone instead of UTC.
  timezone?: string;
  duration_minutes?: number;
  address_line1: string;
  address_line2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  items: BookingItem[];
  customer_note?: string | null;
  customer_note_images?: string[] | null;
  currency?: string;
  // Customer opted into a backup professional at signup.
  accept_backup?: boolean;
  // Customer-nominated preferred backup pros (ordered). Snapshotted on
  // the subscription + each spawned cycle's reassignment search.
  secondary_professional_ids?: string[];
  // Pricing rate-lock — see CreateOneOffPayload for the contract.
  pricing_version?: string;
};

export type SubscriptionCheckoutResponse = {
  status: boolean;
  id: string;
  checkout_url: string | null;
  stripe_session_id: string | null;
  amount_per_cycle: number;
  currency: string;
  frequency: 'DAILY' | 'WEEKLY' | 'FORTNIGHTLY' | 'MONTHLY';
  message: string;
};

export async function createSubscriptionCheckout(
  payload: CreateSubscriptionPayload,
) {
  const res = await customerApi.post(
    '/customer/me/subscriptions/checkout',
    payload,
  );
  return res.data as SubscriptionCheckoutResponse;
}

export async function confirmSubscriptionCheckout(sessionId: string) {
  const res = await customerApi.get(
    `/customer/me/subscriptions/confirm/${encodeURIComponent(sessionId)}`,
  );
  return res.data as {
    status: boolean;
    id: string;
    subscription_status: string;
    amount_per_cycle: number;
    currency: string;
    frequency: string;
    next_billing_at: string | null;
  };
}

export async function listMySubscriptions(opts?: {
  limit?: number;
  offset?: number;
}) {
  const res = await customerApi.get('/customer/me/subscriptions', {
    params: {
      limit: opts?.limit ?? 20,
      offset: opts?.offset ?? 0,
    },
  });
  return res.data as {
    status: boolean;
    data: any[];
    total_count: number;
  };
}

export async function getMySubscriptionDetail(id: string) {
  const res = await customerApi.get(
    `/customer/me/subscriptions/info/${encodeURIComponent(id)}`,
  );
  return res.data as { status: boolean; info: any; message: string };
}

// ----- Per-occurrence add-on overrides -----
// Lets a customer swap / add / remove the extras for one specific
// upcoming visit on a recurring booking, without touching the parent
// subscription. The spawner reads the matching override when
// invoice.paid spawns the child booking for that cycle's date.
export type SubscriptionOverride = {
  id: string;
  scheduled_date: string; // YYYY-MM-DD
  items_override: Array<{
    service_addon_id: string;
    quantity: number;
    properties?: Record<string, any> | null;
  }>;
  customer_note: string | null;
  status: 'PENDING' | 'APPLIED' | 'CANCELLED';
  applied_to_booking_id: string | null;
  applied_at: string | null;
  created_at: string;
  updated_at: string;
};

export async function listSubscriptionOverrides(subscriptionId: string) {
  const res = await customerApi.get(
    `/customer/me/subscriptions/${encodeURIComponent(subscriptionId)}/overrides`,
  );
  return res.data as { status: boolean; data: SubscriptionOverride[] };
}

export async function createSubscriptionOverride(
  subscriptionId: string,
  payload: {
    scheduled_date: string;
    items: Array<{
      service_addon_id: string;
      quantity?: number;
      properties?: Record<string, any> | null;
    }>;
    customer_note?: string | null;
  },
) {
  const res = await customerApi.post(
    `/customer/me/subscriptions/${encodeURIComponent(subscriptionId)}/overrides`,
    payload,
  );
  return res.data as { status: boolean; id: string; message: string };
}

export async function updateSubscriptionOverride(
  overrideId: string,
  payload: {
    items?: Array<{
      service_addon_id: string;
      quantity?: number;
      properties?: Record<string, any> | null;
    }>;
    customer_note?: string | null;
  },
) {
  const res = await customerApi.put(
    `/customer/me/subscriptions/overrides/${encodeURIComponent(overrideId)}`,
    payload,
  );
  return res.data as { status: boolean; message: string };
}

export async function cancelSubscriptionOverride(overrideId: string) {
  const res = await customerApi.delete(
    `/customer/me/subscriptions/overrides/${encodeURIComponent(overrideId)}`,
  );
  return res.data as { status: boolean; message: string };
}

// ============================================================
// Reviews
// ============================================================

export type BookingReview = {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
};

export async function submitBookingReview(
  bookingId: string,
  payload: { rating: number; comment?: string | null },
) {
  const res = await customerApi.post(
    `/customer/me/bookings/${encodeURIComponent(bookingId)}/review`,
    payload,
  );
  return res.data as {
    status: boolean;
    id: string;
    rating: number;
    message: string;
  };
}

export async function getBookingReview(bookingId: string) {
  const res = await customerApi.get(
    `/customer/me/bookings/${encodeURIComponent(bookingId)}/review`,
  );
  return res.data as { status: boolean; info: BookingReview | null };
}

// Customer's "Report Problem" — files a contact_messages row tied
// to this booking so admin picks it up on the contact-messages
// triage queue. Body is required; subject defaults server-side.
export async function reportBookingProblem(
  bookingId: string,
  payload: { message: string; subject?: string },
) {
  const res = await customerApi.post(
    `/customer/me/bookings/${encodeURIComponent(bookingId)}/report`,
    payload,
  );
  return res.data as { status: boolean; message: string };
}

export type PublicReview = {
  id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  customer: { first_name: string | null; last_name: string | null } | null;
  service: { id: string; name: string } | null;
};

// (listPublicReviewsForPro is defined above near the public-catalog
// section — second declaration was a leftover from an earlier merge.)

export async function cancelSubscription(
  id: string,
  opts?: { at_period_end?: boolean; reason?: string },
) {
  const res = await customerApi.put(
    `/customer/me/subscriptions/${encodeURIComponent(id)}/cancel`,
    {
      at_period_end: opts?.at_period_end ?? true,
      reason: opts?.reason ?? null,
    },
  );
  return res.data as {
    status: boolean;
    at_period_end: boolean;
    message: string;
  };
}
